<template>
  <div class="ppa-review-summary">
    <div
      v-for="item in items"
      :key="item.label"
      class="ppa-review-summary__cell"
    >
      <div class="ppa-review-summary__label eyebrow-label eyebrow-label--sm">
        {{ item.label }}
      </div>
      <div class="ppa-review-summary__value">{{ item.value }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  ActionInstructions,
  PortfolioInstructions,
  RegistryDevice,
} from "@/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import {
  determineAllocationMode,
  determineLimitGranularity,
} from "@/dashboard/production_strategy/overview/PPA/ppaPortfolioUtils";

const props = defineProps<{
  actionInstructions: ActionInstructions;
  portfolioInstructions: PortfolioInstructions;
  registryDevices: RegistryDevice[];
  usesCnmcIssuance: boolean;
}>();

const { t } = useI18n();

const counterpartyValue = computed(() => {
  const ai = props.actionInstructions;
  if (ai.transaction_type === "SEND" && ai.send_template) {
    const name = ai.send_template.recipient_company_name?.trim();
    const country = ai.send_template.recipient_registry_country?.trim();
    if (!name && !country) return t("production_strategy.not_set");
    return [name, country].filter(Boolean).join(" · ");
  }
  if (ai.transaction_type === "CANCEL" && ai.cancel_statement) {
    const name = ai.cancel_statement.beneficiary_name?.trim();
    return name
      ? t("production_strategy.review_cancel_for", { name })
      : t("production_strategy.review_cancel_generic");
  }
  return t("production_strategy.not_set");
});

const allocationValue = computed(() => {
  const limits = props.portfolioInstructions.limits ?? [];
  const mode = determineAllocationMode(
    props.portfolioInstructions.allocate_percentage,
    limits
  );
  if (mode === "all") {
    return t("portfolio_instructions.allocate_all");
  }
  if (mode === "percentage") {
    const pct = Number(props.portfolioInstructions.allocate_percentage ?? 0);
    return `${pct}%`;
  }
  const granularity = determineLimitGranularity(limits);
  const granularityLabel = t(
    `portfolio_instructions.granularity_${granularity}`
  );
  return `${granularityLabel} · ${t("production_strategy.review_n_periods", { count: limits.length })}`;
});

const devicesValue = computed(() => {
  const ids = props.portfolioInstructions.registry_device_ids ?? [];
  if (ids.length === 0) return t("production_strategy.no_devices_selected");
  const byId = new Map(props.registryDevices.map((d) => [d.id.toString(), d]));
  // device_power is stored in kW everywhere (backend validators, UI labels);
  // the review summary renders MW, so convert before summing.
  const totalMw = ids.reduce((sum, id) => {
    const device = byId.get(id.toString());
    const kw = Number(device?.device_power ?? 0);
    const mw = Number.isFinite(kw) ? kw / 1000 : 0;
    return sum + mw;
  }, 0);
  const rounded = Math.round(totalMw * 10) / 10;
  return t("production_strategy.review_devices_summary", {
    count: ids.length,
    mw: rounded,
  });
});

const timingKeyByValue: Record<string, string> = {
  YEARLY: "production_strategy.yearly",
  HALF_YEAR: "production_strategy.half_year",
  QUARTERLY: "production_strategy.quarterly",
  MONTHLY: "production_strategy.monthly",
  FIXED_DATES: "production_strategy.fixed_dates",
};

const executionValue = computed(() => {
  if (props.usesCnmcIssuance) {
    return t("cnmc.ppa_execution_summary");
  }
  const ai = props.actionInstructions;
  if (!ai.delivery_timing) return t("production_strategy.not_set");
  const timingKey = timingKeyByValue[ai.delivery_timing];
  const timingLabel = timingKey ? t(timingKey) : ai.delivery_timing;
  const methodLabel =
    ai.delivery_method === "MANUAL"
      ? t("production_strategy.manual_short")
      : t("production_strategy.automatic_short");
  return `${timingLabel} · ${methodLabel}`;
});

const items = computed(() => [
  {
    label: t("production_strategy.review_counterparty_label"),
    value: counterpartyValue.value,
  },
  {
    label: t("production_strategy.review_allocation_label"),
    value: allocationValue.value,
  },
  {
    label: t("production_strategy.review_devices_label"),
    value: devicesValue.value,
  },
  {
    label: t("production_strategy.review_execution_label"),
    value: executionValue.value,
  },
]);
</script>

<style scoped lang="scss">
.ppa-review-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;
  padding: 14px 16px;
  background: var(--phoenix-gray-50);
  border: 1px solid var(--phoenix-border-color-translucent);
  border-radius: 8px;
  font-size: 13px;
}

.ppa-review-summary__cell {
  min-width: 0;
}

.ppa-review-summary__label {
  margin-bottom: 2px;
}

.ppa-review-summary__value {
  color: var(--phoenix-gray-1000);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 576px) {
  .ppa-review-summary {
    grid-template-columns: 1fr;
  }
}
</style>
